import type { CalendarFilters, ResolvedOccurrence } from "@dream/domain";
import { type QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { ApiError, api, type Serialized, unwrap } from "../api/client.ts";
import type { GoogleOverlayEvent } from "../calendar/adapter/mapping.ts";
import { parseGoogleAttendees } from "../lib/people.ts";

/**
 * Consultas e mutações do calendário.
 *
 * As respostas da API trazem instantes em ISO; a desserialização para `Date`
 * acontece uma única vez aqui, para que nenhum componente precise lembrar de
 * converter — o que seria fonte garantida de bug de fuso.
 */

export interface CalendarQueryParams {
  start: string;
  end: string;
  filters: CalendarFilters;
}

interface RawOccurrence
  extends Omit<ResolvedOccurrence, "startsAt" | "endsAt" | "occurrenceStart" | "publishAt"> {
  startsAt: string;
  endsAt: string;
  occurrenceStart: string;
  publishAt: string | null;
}

function hydrate(raw: RawOccurrence): ResolvedOccurrence {
  return {
    ...raw,
    startsAt: new Date(raw.startsAt),
    endsAt: new Date(raw.endsAt),
    occurrenceStart: new Date(raw.occurrenceStart),
    publishAt: raw.publishAt === null ? null : new Date(raw.publishAt),
  };
}

/**
 * Filtros → query string.
 *
 * `start` e `end` são obrigatórios no schema da rota, então o tipo de retorno
 * os declara explicitamente: um `Record<string, string>` genérico não satisfaz
 * a assinatura do Eden, e o erro apareceria só na chamada.
 */
interface CalendarQueryString {
  start: string;
  end: string;
  clientIds?: string;
  projectIds?: string;
  typeIds?: string;
  assigneeIds?: string;
  statuses?: string;
  tagIds?: string;
  search?: string;
}

function toQueryParams(params: CalendarQueryParams): CalendarQueryString {
  const { filters } = params;
  const query: CalendarQueryString = { start: params.start, end: params.end };

  if (filters.clientIds.length > 0) query.clientIds = filters.clientIds.join(",");
  if (filters.projectIds.length > 0) query.projectIds = filters.projectIds.join(",");
  if (filters.typeIds.length > 0) query.typeIds = filters.typeIds.join(",");
  if (filters.assigneeIds.length > 0) query.assigneeIds = filters.assigneeIds.join(",");
  if (filters.statuses.length > 0) query.statuses = filters.statuses.join(",");
  if (filters.tagIds.length > 0) query.tagIds = filters.tagIds.join(",");
  if (filters.search !== null && filters.search !== "") query.search = filters.search;

  return query;
}

export function adminCalendarKey(params: CalendarQueryParams): QueryKey {
  return ["calendar", "admin", params.start, params.end, params.filters];
}

export function publicCalendarKey(slug: string, params: CalendarQueryParams): QueryKey {
  return ["calendar", "public", slug, params.start, params.end, params.filters];
}

export interface CalendarData {
  occurrences: ResolvedOccurrence[];
  truncated: boolean;
}

export function useAdminCalendar(params: CalendarQueryParams, enabled = true) {
  return useQuery<CalendarData, ApiError>({
    queryKey: adminCalendarKey(params),
    enabled,
    // Mantém o mês anterior visível durante a navegação, em vez de piscar
    // vazio a cada troca de período.
    placeholderData: (previous) => previous,
    staleTime: 15_000,
    queryFn: async () => {
      const response = await api.api.admin.calendar.get({
        query: toQueryParams(params),
      });
      const data = unwrap(response);
      return {
        occurrences: (data.occurrences as unknown as RawOccurrence[]).map(hydrate),
        truncated: data.truncated,
      };
    },
  });
}

function shareAuth(
  token: string,
  password?: string,
): {
  query: { token: string };
  headers?: { "X-Dream-Share-Password": string };
} {
  if (password === undefined || password === "") {
    return { query: { token } };
  }
  return {
    query: { token },
    headers: { "X-Dream-Share-Password": password },
  };
}

export function usePublicCalendar(
  slug: string,
  token: string,
  params: CalendarQueryParams,
  enabled = true,
  password?: string,
) {
  return useQuery<CalendarData, ApiError>({
    queryKey: publicCalendarKey(slug, params),
    enabled: enabled && slug !== "" && token !== "",
    placeholderData: (previous) => previous,
    staleTime: 15_000,
    // Link revogado, expirado ou token inválido não se resolve tentando de
    // novo; insistir só geraria ruído e consumiria a cota de rate limit.
    retry: (count, error) =>
      error instanceof ApiError && (error.isGone || error.status === 404 || error.isForbidden)
        ? false
        : count < 2,
    queryFn: async () => {
      const auth = shareAuth(token, password);
      const response = await api.api.public({ slug }).calendar.get({
        query: { ...toQueryParams(params), token: auth.query.token },
        ...(auth.headers === undefined ? {} : { headers: auth.headers }),
      });
      const data = unwrap(response);
      return {
        occurrences: (data.occurrences as unknown as RawOccurrence[]).map(hydrate),
        truncated: data.truncated,
      };
    },
  });
}

export function usePublicContext(slug: string, token: string, password?: string) {
  return useQuery({
    queryKey: ["public-context", slug, token !== ""],
    enabled: slug !== "" && token !== "",
    retry: (count, error) =>
      error instanceof ApiError && (error.isGone || error.status === 404 || error.status === 401)
        ? false
        : count < 2,
    queryFn: async () => {
      const auth = shareAuth(token, password);
      const response = await api.api.public({ slug }).context.get({
        query: auth.query,
        ...(auth.headers === undefined ? {} : { headers: auth.headers }),
      });
      return unwrap(response);
    },
  });
}

export function usePublicDeliverable(
  slug: string,
  token: string,
  id: string | null,
  password?: string,
) {
  return useQuery({
    queryKey: ["public-deliverable", slug, id],
    enabled: id !== null && slug !== "" && token !== "",
    retry: (count, error) =>
      error instanceof ApiError && (error.isGone || error.status === 404 || error.isForbidden)
        ? false
        : count < 2,
    queryFn: async () => {
      const auth = shareAuth(token, password);
      const data = unwrap(
        await api.api
          .public({ slug })
          .deliverables({ id: id as string })
          .get({
            query: auth.query,
            ...(auth.headers === undefined ? {} : { headers: auth.headers }),
          }),
      );
      return data as unknown as Serialized<typeof data>;
    },
  });
}

export function usePublicAttachments(
  slug: string,
  token: string,
  id: string | null,
  password?: string,
) {
  return useQuery({
    queryKey: ["public-attachments", slug, id],
    enabled: id !== null && slug !== "" && token !== "",
    queryFn: async () => {
      const auth = shareAuth(token, password);
      return unwrap(
        await api.api
          .public({ slug })
          .deliverables({ id: id as string })
          .attachments.get({
            query: auth.query,
            ...(auth.headers === undefined ? {} : { headers: auth.headers }),
          }),
      ) as unknown as Array<{
        id: string;
        fileName: string;
        mimeType: string;
        sizeBytes: number;
        createdAt: string;
      }>;
    },
  });
}

export function clientViewCalendarKey(handle: string, params: CalendarQueryParams): QueryKey {
  return ["calendar", "client-view", handle, params.start, params.end, params.filters];
}

export function useClientViewPreview(handle: string) {
  return useQuery({
    queryKey: ["client-view-preview", handle],
    enabled: handle !== "",
    retry: (count, error) =>
      error instanceof ApiError && (error.status === 404 || error.isGone) ? false : count < 2,
    queryFn: async () => unwrap(await api.api.portal({ handle }).get()),
  });
}

export function useClientViewContext(handle: string, enabled = true) {
  return useQuery({
    queryKey: ["client-view-context", handle],
    enabled: enabled && handle !== "",
    retry: (count, error) =>
      error instanceof ApiError &&
      (error.isGone || error.status === 404 || error.status === 401 || error.isForbidden)
        ? false
        : count < 2,
    queryFn: async () => unwrap(await api.api.portal({ handle }).context.get()),
  });
}

export function useClientViewCalendar(handle: string, params: CalendarQueryParams, enabled = true) {
  return useQuery<CalendarData, ApiError>({
    queryKey: clientViewCalendarKey(handle, params),
    enabled: enabled && handle !== "",
    placeholderData: (previous) => previous,
    staleTime: 15_000,
    retry: (count, error) =>
      error instanceof ApiError && (error.isGone || error.status === 404 || error.isForbidden)
        ? false
        : count < 2,
    queryFn: async () => {
      const response = await api.api.portal({ handle }).calendar.get({
        query: toQueryParams(params),
      });
      const data = unwrap(response);
      return {
        occurrences: (data.occurrences as unknown as RawOccurrence[]).map(hydrate),
        truncated: data.truncated,
      };
    },
  });
}

export function useClientViewDeliverable(handle: string, id: string | null) {
  return useQuery({
    queryKey: ["client-view-deliverable", handle, id],
    enabled: id !== null && handle !== "",
    retry: (count, error) =>
      error instanceof ApiError && (error.isGone || error.status === 404 || error.isForbidden)
        ? false
        : count < 2,
    queryFn: async () => {
      const data = unwrap(
        await api.api
          .portal({ handle })
          .deliverables({ id: id as string })
          .get(),
      );
      return data as unknown as Serialized<typeof data>;
    },
  });
}

export function useClientViewAttachments(handle: string, id: string | null) {
  return useQuery({
    queryKey: ["client-view-attachments", handle, id],
    enabled: id !== null && handle !== "",
    queryFn: async () =>
      unwrap(
        await api.api
          .portal({ handle })
          .deliverables({ id: id as string })
          .attachments.get(),
      ) as unknown as Array<{
        id: string;
        fileName: string;
        mimeType: string;
        sizeBytes: number;
        createdAt: string;
      }>,
  });
}

export function useClientViewUnlock(handle: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pin: string) =>
      unwrap(await api.api.portal({ handle }).session.post({ pin })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["client-view-context", handle] });
      void queryClient.invalidateQueries({ queryKey: ["calendar", "client-view", handle] });
    },
  });
}

/** Catálogo administrativo. Cacheado por mais tempo: muda com pouca frequência. */
export function useAdminCatalog() {
  const clients = useQuery({
    queryKey: ["catalog", "clients"],
    staleTime: 60_000,
    queryFn: async () => unwrap(await api.api.admin.clients.get()),
  });

  const projects = useQuery({
    queryKey: ["catalog", "projects"],
    staleTime: 60_000,
    queryFn: async () => unwrap(await api.api.admin.projects.get()),
  });

  const types = useQuery({
    queryKey: ["catalog", "types"],
    staleTime: 300_000,
    queryFn: async () => unwrap(await api.api.admin.types.get()),
  });

  const team = useQuery({
    queryKey: ["catalog", "team"],
    staleTime: 300_000,
    queryFn: async () => unwrap(await api.api.admin.team.get()),
  });

  return {
    clients: clients.data ?? [],
    projects: projects.data ?? [],
    types: types.data ?? [],
    team: team.data ?? [],
    isLoading: clients.isLoading || types.isLoading,
    error: clients.error ?? projects.error ?? types.error ?? team.error,
  };
}

/** Invalida todo o cache de calendário. Usado pelas notificações e reconexões. */
export function useInvalidateCalendar() {
  const queryClient = useQueryClient();
  const lastAtMs = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  return useCallback(
    (at?: string) => {
      if (typeof at === "string" && at.length > 0) {
        const parsed = Date.parse(at);
        if (Number.isFinite(parsed) && parsed < lastAtMs.current) return;
        if (Number.isFinite(parsed)) lastAtMs.current = parsed;
      }
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ["calendar"], refetchType: "active" });
        void queryClient.invalidateQueries({
          queryKey: ["google-calendar-events"],
          refetchType: "active",
        });
        void queryClient.invalidateQueries({
          queryKey: ["google-calendar"],
          refetchType: "active",
        });
      }, 120);
    },
    [queryClient],
  );
}

export function googleCalendarEventsKey(start: string, end: string): QueryKey {
  return ["google-calendar-events", start, end];
}

export function useGoogleCalendarEvents(range: { start: string; end: string }, enabled = true) {
  return useQuery<GoogleOverlayEvent[], ApiError>({
    queryKey: googleCalendarEventsKey(range.start, range.end),
    enabled,
    staleTime: 2_000,
    refetchInterval: 45_000,
    retry: (count, error) =>
      error instanceof ApiError && (error.isUnauthorized || error.status === 404)
        ? false
        : count < 2,
    queryFn: async ({ signal }) => {
      const data = unwrap(
        await api.api.admin["google-calendar"].events.get({
          query: { start: range.start, end: range.end },
          fetch: { signal, credentials: "include" },
        }),
      );
      const events = data.events.map((event) => {
        const details = parseGoogleAttendees(
          "attendeeDetails" in event && Array.isArray(event.attendeeDetails)
            ? event.attendeeDetails
            : "attendees" in event
              ? event.attendees
              : [],
        );
        return {
          id: event.id,
          title: event.title,
          startsAt: event.startsAt,
          endsAt: event.endsAt,
          isAllDay: event.isAllDay,
          htmlLink: event.htmlLink,
          calendarName: event.calendarName,
          color: event.color,
          foreground: "foreground" in event ? (event.foreground ?? null) : null,
          colorId: "colorId" in event ? (event.colorId ?? null) : null,
          location: "location" in event ? (event.location ?? null) : null,
          hangoutLink: "hangoutLink" in event ? (event.hangoutLink ?? null) : null,
          transparency: "transparency" in event ? (event.transparency ?? null) : null,
          eventType: "eventType" in event ? (event.eventType ?? null) : null,
          attendees: details.map((person) => person.email),
          attendeeDetails: details,
          calendarEmail:
            "calendarEmail" in event && typeof event.calendarEmail === "string"
              ? event.calendarEmail
              : undefined,
        };
      });
      return events.sort((a, b) => {
        const delta = Date.parse(a.startsAt) - Date.parse(b.startsAt);
        if (delta !== 0) return delta;
        return a.title.localeCompare(b.title, "pt-BR");
      });
    },
  });
}

export function useGoogleCalendarStatus(enabled = true) {
  return useQuery({
    queryKey: ["google-calendar"],
    enabled,
    staleTime: 15_000,
    refetchInterval: 90_000,
    queryFn: async () => unwrap(await api.api.admin["google-calendar"].get()),
  });
}

export interface MoveVariables {
  deliverableId: string;
  version: number;
  startsAt: string;
  endsAt: string;
  occurrenceStart?: string | undefined;
  isAllDay?: boolean;
  /** Rollback visual do FullCalendar, chamado se a persistência falhar. */
  revert?: (() => void) | undefined;
}

/**
 * Arraste.
 *
 * Sem update otimista: o FullCalendar já move o elemento na tela antes de
 * chamar a mutação, então o "otimismo" é visual e nativo. O que importa é o
 * caminho de falha — `revert()` devolve o evento à posição original, e só
 * então o cache é invalidado para trazer o estado real do servidor.
 */
export function useMoveDeliverable() {
  const queryClient = useQueryClient();

  return useMutation<unknown, ApiError, MoveVariables>({
    mutationFn: async (variables) => {
      const response = await api.api.admin.deliverables({ id: variables.deliverableId }).move.post({
        version: variables.version,
        startsAt: variables.startsAt,
        endsAt: variables.endsAt,
        ...(variables.occurrenceStart === undefined
          ? {}
          : { occurrenceStart: variables.occurrenceStart }),
        ...(variables.isAllDay === undefined ? {} : { isAllDay: variables.isAllDay }),
      });
      return unwrap(response);
    },
    onError: (_error, variables) => {
      variables.revert?.();
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
    },
  });
}

export interface ResizeVariables {
  deliverableId: string;
  version: number;
  endsAt: string;
  occurrenceStart?: string | undefined;
  revert?: (() => void) | undefined;
}

export function useResizeDeliverable() {
  const queryClient = useQueryClient();

  return useMutation<unknown, ApiError, ResizeVariables>({
    mutationFn: async (variables) => {
      const response = await api.api.admin
        .deliverables({ id: variables.deliverableId })
        .resize.post({
          version: variables.version,
          endsAt: variables.endsAt,
          ...(variables.occurrenceStart === undefined
            ? {}
            : { occurrenceStart: variables.occurrenceStart }),
        });
      return unwrap(response);
    },
    onError: (_error, variables) => {
      variables.revert?.();
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
    },
  });
}

/**
 * Detalhe de uma entrega.
 *
 * O retorno é declarado como `Serialized<...>`: o Eden tipa as datas como
 * `Date` porque é isso que o handler devolve, mas elas atravessam JSON e chegam
 * como string ISO. A conversão acontece aqui, na fronteira de rede, para que
 * nenhum componente precise decidir se um campo é `Date` ou `string`.
 */
export function useDeliverable(id: string | null) {
  return useQuery({
    queryKey: ["deliverable", id],
    enabled: id !== null,
    queryFn: async () => {
      const data = unwrap(await api.api.admin.deliverables({ id: id as string }).get());
      return data as unknown as Serialized<typeof data>;
    },
  });
}

export function useActivity(entityId: string | null) {
  return useQuery({
    queryKey: ["activity", entityId],
    enabled: entityId !== null,
    queryFn: async () =>
      unwrap(
        await api.api.admin.activity.get({
          query: { entityId: entityId as string },
        }),
      ),
  });
}
